import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '@/store/useTheme'
import { useSession } from '@/store/useSession'
import type { WotSelection } from '@/store/sessionTypes'
import {
  includesMoodCheck,
  resolveSessionStartStep,
  shouldSkipToCanvas,
  stepAfterCheckin,
  type SessionStartOptions,
  type SessionStartStep,
} from '@/utils/sessionStartFlow'
import { readPersistedSessionStartOptions } from '@/utils/sessionStartOptionsStorage'
import { panasScoreFromPartialAnswers } from '@/utils/panas'
import styles from './SessionStartPage.module.css'

const WOT_FADE_MS = 450
const OPACITY_FADE_MS = 600
const FADE_IN_DELAY_MS = 50
const SCORE_HOLD_MS = 3000

type FlowStep = SessionStartStep

function resolveSessionOptions(practitioner: {
  includeWotCheck: boolean
  includeWarmup: boolean
  moodCheckMode: SessionStartOptions['moodCheckMode']
}): SessionStartOptions {
  return (
    readPersistedSessionStartOptions() ?? {
      includeWotCheck: practitioner.includeWotCheck,
      includeWarmup: practitioner.includeWarmup,
      moodCheckMode: practitioner.moodCheckMode,
    }
  )
}

function initialStep(
  practiceDone: boolean,
  wot: WotSelection | null,
  panasDone: boolean,
  options: SessionStartOptions,
): FlowStep {
  return resolveSessionStartStep(practiceDone, wot, panasDone, options)
}

const WOT_OPTIONS: { id: WotSelection; label: string; subtitle: string }[] = [
  { id: 'hyper', label: 'Hyper', subtitle: 'Anxious, busy, hard to settle'     },
  { id: 'present',   label: 'Present',   subtitle: 'Grounded and ready'                },
  { id: 'flat',      label: 'Flat',      subtitle: 'Low, disconnected, hard to engage' },
]

const CLIENT_PRACTICES = [
  { id: 'breathing',     label: 'A breathing exercise', subtitle: 'A slow 4:6 breath to help you settle', path: '/breathe/four-six' },
  { id: 'box-breathing', label: 'Box breathing',        subtitle: 'Equal counts of breath to steady and focus', path: '/breathe/box' },
  { id: 'grounding',     label: 'A grounding practice', subtitle: 'More ways to help you settle', path: '/grounding' },
] as const

type PracticeId = typeof CLIENT_PRACTICES[number]['id']

// ── PANAS data ────────────────────────────────────────────────────────────
const PANAS_ITEMS: { word: string; type: 'positive' | 'negative' }[] = [
  { word: 'Interested',   type: 'positive' },
  { word: 'Distressed',   type: 'negative' },
  { word: 'Excited',      type: 'positive' },
  { word: 'Upset',        type: 'negative' },
  { word: 'Strong',       type: 'positive' },
  { word: 'Guilty',       type: 'negative' },
  { word: 'Scared',       type: 'negative' },
  { word: 'Hostile',      type: 'negative' },
  { word: 'Enthusiastic', type: 'positive' },
  { word: 'Proud',        type: 'positive' },
  { word: 'Irritable',    type: 'negative' },
  { word: 'Alert',        type: 'positive' },
  { word: 'Ashamed',      type: 'negative' },
  { word: 'Inspired',     type: 'positive' },
  { word: 'Nervous',      type: 'negative' },
  { word: 'Determined',   type: 'positive' },
  { word: 'Attentive',    type: 'positive' },
  { word: 'Jittery',      type: 'negative' },
  { word: 'Active',       type: 'positive' },
  { word: 'Afraid',       type: 'negative' },
]

const SCALE_LABELS: Record<number, string> = {
  1: 'Very slightly or not at all',
  2: 'A little',
  3: 'Moderately',
  4: 'Quite a bit',
  5: 'Extremely',
}

const SCORE_WORDS: Record<number, string> = {
  1: 'ONE', 2: 'TWO', 3: 'THREE', 4: 'FOUR', 5: 'FIVE',
}

const FLIP_MS = 420

// ── Centred neutral content card (WoT, benchmark, PANAS) ────────────────
function ContentCard({
  children,
  exiting,
}: {
  children: React.ReactNode
  exiting?: boolean
}) {
  return (
    <div className={styles.contentCardWrap}>
      <div className={`${styles.contentCard} ${exiting ? styles.contentCardExit : ''}`}>
        {children}
      </div>
    </div>
  )
}

// ── Window of Tolerance — always visible once selected ───────────────────
function WoTSection({
  selected,
  onSelect,
  locked,
}: {
  selected: WotSelection | null
  onSelect: (id: WotSelection) => void
  locked:   boolean
}) {
  return (
    <section className={styles.wotSection} aria-labelledby="wot-heading">
      <header className={styles.sectionHeader}>
        <h1 id="wot-heading" className={styles.heading}>
          Before we begin, let's check in.
        </h1>
        <p className={styles.subtitle}>Where are you right now?</p>
      </header>

      <div
        className={`${styles.wotCards} ${locked ? styles.wotCardsLocked : ''}`}
        role="group"
        aria-label="Window of Tolerance check"
      >
        {WOT_OPTIONS.map(opt => (
          <button
            key={opt.id}
            type="button"
            className={styles.wotCard}
            data-selected={selected === opt.id}
            aria-pressed={selected === opt.id}
            onClick={() => onSelect(opt.id)}
            tabIndex={locked ? -1 : undefined}
          >
            <span className={styles.wotLabel}>{opt.label}</span>
            <span className={styles.wotSub}>{opt.subtitle}</span>
          </button>
        ))}
      </div>
    </section>
  )
}

// ── Benchmark intro — full step before PANAS ────────────────────────────
function BenchmarkIntro({ onContinue }: { onContinue: () => void }) {
  return (
    <section className={styles.benchmarkIntro} aria-labelledby="benchmark-heading">
      <h1 id="benchmark-heading" className={styles.benchmarkHeading}>
        How's your mood today?
      </h1>
      <p className={styles.benchmarkSub}>
        There are no right or wrong answers.
      </p>
      <button type="button" className={styles.continueBtn} onClick={onContinue}>
        Continue
      </button>
    </section>
  )
}

// ── Practice menu ─────────────────────────────────────────────────────────
function PracticeMenu({
  onSelect,
  onContinueWhenReady,
}: {
  onSelect:               (id: PracticeId) => void
  onContinueWhenReady:    () => void
}) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const t = window.setTimeout(() => setVisible(true), FADE_IN_DELAY_MS)
    return () => window.clearTimeout(t)
  }, [])

  return (
    <section
      className={`${styles.practiceSection} ${styles.fadeOpacity} ${visible ? styles.fadeOpacityIn : ''}`}
      aria-labelledby="practices-heading"
    >
      <h2 id="practices-heading" className={styles.practiceHeading}>
        Let's do a simple practice ready for the session.
      </h2>

      <div className={styles.cardGrid} role="group" aria-label="Practice options">
        {CLIENT_PRACTICES.map(p => (
          <button
            key={p.id}
            type="button"
            className={styles.practiceOptionCard}
            onClick={() => onSelect(p.id)}
          >
            <span className={styles.cardLabel}>{p.label}</span>
          </button>
        ))}
      </div>

      <button type="button" className={styles.canvasLink} onClick={onContinueWhenReady}>
        Continue when you're ready
      </button>
    </section>
  )
}

// ── Post-practice landing ─────────────────────────────────────────────────
function PostPracticeScreen({
  onTrySomethingElse,
  onContinue,
}: {
  onTrySomethingElse:  () => void
  onContinue:          () => void
}) {
  return (
    <div className={styles.postPractice} aria-live="polite">
      <p className={styles.postPracticeMsg}>
        Good. How are you feeling now? Ready to go or would you like to try something else?
      </p>
      <div className={styles.postPracticeActions}>
        <button type="button" className={styles.continueBtn} onClick={onTrySomethingElse}>
          Try something else
        </button>
        <button type="button" className={styles.continueToCanvasLink} onClick={onContinue}>
          Continue to canvas →
        </button>
      </div>
    </div>
  )
}

// ── Brief score reveal after PANAS ───────────────────────────────────────
function ScoreBrief({
  score,
  onDone,
}: {
  score:  number
  onDone: () => void
}) {
  const [visible, setVisible] = useState(false)
  const hasShownRef = useRef(false)
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone

  useEffect(() => {
    const t = window.setTimeout(() => {
      hasShownRef.current = true
      setVisible(true)
    }, FADE_IN_DELAY_MS)
    return () => window.clearTimeout(t)
  }, [])

  useEffect(() => {
    if (!visible) return
    const hold = window.setTimeout(() => setVisible(false), SCORE_HOLD_MS)
    return () => window.clearTimeout(hold)
  }, [visible])

  useEffect(() => {
    if (visible || !hasShownRef.current) return
    const done = window.setTimeout(() => onDoneRef.current(), OPACITY_FADE_MS)
    return () => window.clearTimeout(done)
  }, [visible])

  return (
    <div
      className={`${styles.scoreBrief} ${styles.fadeOpacity} ${visible ? styles.fadeOpacityIn : ''}`}
      aria-live="polite"
      aria-label={`Mood check score: ${score} percent`}
    >
      <p className={styles.scoreBriefValue}>{score}%</p>
    </div>
  )
}

// ── PANAS mood check (inline practice) ────────────────────────────────────
function PANASStep({
  onComplete,
  onSkip,
}: {
  onComplete: () => void
  onSkip: () => void
}) {
  const { setClientPanasScore } = useSession()

  const [frontWord,     setFrontWord]     = useState(PANAS_ITEMS[0].word)
  const [frontType,     setFrontType]     = useState(PANAS_ITEMS[0].type)
  const [selectedScore, setSelectedScore] = useState<number | null>(null)
  const [isFlipped,     setIsFlipped]     = useState(false)
  const [navKey,        setNavKey]        = useState(0)
  const [navDir,        setNavDir]        = useState<'init' | 'fwd' | 'back'>('init')
  const [busy,          setBusy]          = useState(false)
  const [hoveredValue,  setHoveredValue]  = useState<number | null>(null)
  const [index,         setIndex]         = useState(0)
  const [answers,       setAnswers]       = useState<Record<number, number>>({})
  const [phase,         setPhase]         = useState<'active' | 'score'>('active')
  const [panasExiting,  setPanasExiting]  = useState(false)
  const [score,         setScore]         = useState(0)

  const revealScore = (pct: number) => {
    setScore(pct)
    setClientPanasScore(pct)
    setPanasExiting(true)
    setTimeout(() => setPhase('score'), OPACITY_FADE_MS)
  }

  const goTo = (newIdx: number, dir: 'fwd' | 'back') => {
    if (busy || newIdx < 0 || newIdx >= 20) return
    setBusy(true)
    setFrontWord(PANAS_ITEMS[newIdx].word)
    setFrontType(PANAS_ITEMS[newIdx].type)
    setIndex(newIdx)
    setSelectedScore(answers[newIdx] ?? null)

    if (isFlipped) {
      setIsFlipped(false)
      setTimeout(() => setBusy(false), FLIP_MS + 40)
    } else {
      setNavDir(dir)
      setNavKey(k => k + 1)
      setTimeout(() => setBusy(false), 300)
    }
  }

  const handleAnswer = (value: number) => {
    if (busy) return
    setAnswers(prev => ({ ...prev, [index]: value }))
    setSelectedScore(value)
    if (!isFlipped) {
      setBusy(true)
      setIsFlipped(true)
      setTimeout(() => setBusy(false), FLIP_MS + 40)
    }
  }

  const handleFwd = () => {
    if (!canFwd) return
    if (index === 19) finalize({ ...answers })
    else goTo(index + 1, 'fwd')
  }

  const handleAreaEnter = () => {
    if (!busy && selectedScore !== null && !isFlipped) setIsFlipped(true)
  }
  const handleAreaLeave = () => {
    if (!busy && selectedScore !== null && isFlipped) setIsFlipped(false)
  }
  const handleCardToggle = () => {
    if (!busy && selectedScore !== null) setIsFlipped(f => !f)
  }

  const finalize = (all: Record<number, number>) => {
    let pa = 0, na = 0
    PANAS_ITEMS.forEach((item, i) => {
      const v = all[i] ?? 1
      if (item.type === 'positive') pa += v
      else na += v
    })
    const pct = Math.round(((pa - na) + 40) / 80 * 100)
    revealScore(pct)
  }

  const handleSkipToday = () => {
    const partial = panasScoreFromPartialAnswers(answers)
    if (partial !== null) {
      revealScore(partial)
    } else {
      setPanasExiting(true)
      setTimeout(() => onSkip(), OPACITY_FADE_MS)
    }
  }

  if (phase === 'score') return <ScoreBrief score={score} onDone={onComplete} />

  const currentAnswer = answers[index]
  const canFwd  = currentAnswer !== undefined && !busy
  const canBack = index > 0 && !busy

  const descriptorText =
    hoveredValue  !== null        ? SCALE_LABELS[hoveredValue]
    : currentAnswer !== undefined ? SCALE_LABELS[currentAnswer]
    : ''

  return (
    <div className={`${styles.panas} ${panasExiting ? styles.panasExiting : ''}`}>
      <div className={styles.panasHeader}>
        <p className={styles.panasHeaderLabel}>Mood check</p>
        <p className={styles.panasProgress} aria-label={`Card ${index + 1} of 20`}>
          {index + 1} <span className={styles.panasProgressOf}>/ 20</span>
        </p>
      </div>

      <div className={styles.progressTrack} aria-hidden="true">
        <div
          className={styles.progressFill}
          style={{ width: `${((index + 1) / 20) * 100}%` }}
        />
      </div>

      <div
        className={styles.cardStage}
        onClick={handleCardToggle}
        onMouseEnter={handleAreaEnter}
        onMouseLeave={handleAreaLeave}
        style={{ cursor: selectedScore !== null ? 'pointer' : 'default' }}
        aria-label={selectedScore !== null ? 'Tap to flip card' : undefined}
        role={selectedScore !== null ? 'button' : undefined}
      >
        <div
          key={navKey}
          data-dir={navDir}
          data-flipped={isFlipped ? 'true' : 'false'}
          className={styles.cardContainer}
        >
          <div
            className={styles.cardFront}
            data-type={frontType}
            aria-live="polite"
            aria-atomic="true"
          >
            {frontWord}
          </div>
          <div className={styles.cardBack} data-type={frontType} aria-hidden="true">
            {selectedScore !== null && (
              <>
                <span className={styles.backWord}>{SCORE_WORDS[selectedScore]}</span>
                <span className={styles.backLabel}>{SCALE_LABELS[selectedScore].toUpperCase()}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={styles.interactionArea}>
        <div
          className={styles.responseRow}
          role="group"
          aria-label={`How much do you feel ${frontWord.toLowerCase()} right now?`}
        >
          {([1, 2, 3, 4, 5] as const).map(v => (
            <button
              key={v}
              type="button"
              className={styles.responseBtn}
              data-selected={currentAnswer === v}
              onClick={() => handleAnswer(v)}
              disabled={busy}
              aria-label={SCALE_LABELS[v]}
              aria-pressed={currentAnswer === v}
              onMouseEnter={() => setHoveredValue(v)}
              onMouseLeave={() => setHoveredValue(null)}
              onFocus={()    => setHoveredValue(v)}
              onBlur={()     => setHoveredValue(null)}
            >
              {v}
            </button>
          ))}
        </div>

        <div className={styles.descriptorBox} aria-live="polite" aria-atomic="true">
          <span className={styles.descriptorText}>{descriptorText}</span>
        </div>

        <div className={styles.navRow}>
          <button
            type="button"
            className={styles.navBtn}
            onClick={() => goTo(index - 1, 'back')}
            disabled={!canBack}
            aria-label="Previous word"
          >
            ←
          </button>
          <button
            type="button"
            className={styles.navBtn}
            onClick={handleFwd}
            disabled={!canFwd}
            aria-label={index === 19 ? 'See your score' : 'Next word'}
          >
            →
          </button>
        </div>
      </div>

      <button
        type="button"
        className={styles.skipTodayLink}
        onClick={handleSkipToday}
      >
        Skip this today
      </button>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────

export default function SessionStartPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { palette } = useTheme()
  const { practitioner, clientWotSelection, setClientWotSelection, clientPanasScore } = useSession()

  const sessionOptions = useMemo(
    () => resolveSessionOptions(practitioner),
    [practitioner],
  )

  const [wot, setWot] = useState<WotSelection | null>(clientWotSelection)
  const [panasSkipped, setPanasSkipped] = useState(false)
  const panasSatisfied = clientPanasScore !== null || panasSkipped

  const [step, setStep] = useState<FlowStep>(() =>
    initialStep(
      searchParams.get('practice-done') === '1',
      clientWotSelection,
      panasSatisfied,
      sessionOptions,
    ),
  )
  const [wotExiting, setWotExiting] = useState(false)

  useEffect(() => {
    if (searchParams.get('practice-done') === '1') return
    if (shouldSkipToCanvas(clientWotSelection, panasSatisfied, sessionOptions)) {
      navigate('/session/canvas', { replace: true })
    }
  }, [searchParams, clientWotSelection, panasSatisfied, sessionOptions, navigate])

  useEffect(() => {
    if (searchParams.get('practice-done') === '1') {
      setStep('landing')
    }
  }, [searchParams])

  useEffect(() => {
    if (!wotExiting) return
    const t = setTimeout(() => {
      setWotExiting(false)
      if (shouldSkipToCanvas(wot, panasSatisfied, sessionOptions)) {
        navigate('/session/canvas', { replace: true })
      } else {
        setStep(stepAfterCheckin(panasSatisfied, sessionOptions))
      }
    }, WOT_FADE_MS)
    return () => clearTimeout(t)
  }, [wotExiting, wot, panasSatisfied, sessionOptions, navigate])

  const clearPracticeDoneParam = () => {
    if (!searchParams.has('practice-done')) return
    const next = new URLSearchParams(searchParams)
    next.delete('practice-done')
    setSearchParams(next, { replace: true })
  }

  const handleWotSelect = (id: WotSelection) => {
    if (step !== 'checkin' || wotExiting) return
    setWot(id)
    setClientWotSelection(id)
    setWotExiting(true)
  }

  const handlePracticeSelect = (id: PracticeId) => {
    const practice = CLIENT_PRACTICES.find(p => p.id === id)
    if (!practice) return
    navigate(`${practice.path}?return=${encodeURIComponent('/session/start')}`)
  }

  const handleTrySomethingElse = () => {
    clearPracticeDoneParam()
    setStep('menu')
  }

  const handleContinueToCanvas = () => {
    clearPracticeDoneParam()
    navigate('/session/canvas')
  }

  const advanceAfterPanas = useCallback(() => {
    if (sessionOptions.includeWarmup) {
      setStep('menu')
    } else {
      navigate('/session/canvas')
    }
  }, [sessionOptions.includeWarmup, navigate])

  const handlePanasComplete = useCallback(() => {
    advanceAfterPanas()
  }, [advanceAfterPanas])

  const handlePanasSkip = useCallback(() => {
    setPanasSkipped(true)
    advanceAfterPanas()
  }, [advanceAfterPanas])

  const paletteVars = {
    '--ss-bg':   palette.bg,
    '--ss-text': palette.text,
    '--ss-ring': palette.ring,
  } as React.CSSProperties

  return (
    <main
      className={`${styles.page} ${(step === 'panas' || step === 'benchmark-intro' || step === 'checkin' || wotExiting) ? styles.pageCentered : ''}`}
      style={paletteVars}
      aria-label="Session start"
    >
      {(step === 'checkin' || wotExiting) && sessionOptions.includeWotCheck && (
        <ContentCard exiting={wotExiting}>
          <WoTSection
            selected={wot}
            onSelect={handleWotSelect}
            locked={wotExiting}
          />
        </ContentCard>
      )}

      {step === 'benchmark-intro' && !wotExiting && includesMoodCheck(sessionOptions.moodCheckMode) && (
        <ContentCard>
          <BenchmarkIntro onContinue={() => setStep('panas')} />
        </ContentCard>
      )}

      {step === 'panas' && includesMoodCheck(sessionOptions.moodCheckMode) && (
        <ContentCard>
          <PANASStep onComplete={handlePanasComplete} onSkip={handlePanasSkip} />
        </ContentCard>
      )}

      {(step === 'menu' || step === 'landing') && (
        <div className={styles.inner}>
          {step === 'landing' && (
            <PostPracticeScreen
              onTrySomethingElse={handleTrySomethingElse}
              onContinue={handleContinueToCanvas}
            />
          )}

          {step === 'menu' && sessionOptions.includeWarmup && (
            <PracticeMenu
              onSelect={handlePracticeSelect}
              onContinueWhenReady={() => navigate('/session/canvas')}
            />
          )}
        </div>
      )}
    </main>
  )
}
